/**
 * @spec docs/superpowers/specs/2026-10-15-staking-ui-wallet-actions-design.md §4.1, §5
 * @standard docs/standards/staking-ui-wallet-actions.md#action-catalogue
 *
 * One descriptor per flow, reviewed as a catalogue. Hooks and components never build a
 * WalletAction themselves; they take one from here.
 */
import { getStakingChainId, getSwitchableChain, LEGACY_POLYGON_CONFIG } from '@/lib/chain-registry';
import type { WalletAction } from './types';

export interface ActionCatalogue {
  stake(): WalletAction;
  positionOp(): WalletAction;
  claimPhnm(): WalletAction;
  claimChip(): WalletAction;
  claimUsdt(): WalletAction;
  swap(): WalletAction;
  swapUsdcAuth(): WalletAction;
  delegateUpgrade(): WalletAction;
  gaslessSend(): WalletAction;
  embeddedNftTransfer(): WalletAction;
  chipSend(): WalletAction;
  directSend(chainId: number): WalletAction;
  externalNftTransfer(): WalletAction;
  chipRedeem(): WalletAction;
  walletRelations(): WalletAction;
}

/** EIP-712 bound to the staking chain, submitted by a relay (no gas from the wallet). */
function relayedStakingSignature(): WalletAction {
  return { kind: 'chain-signature', chainId: getStakingChainId(), gasPayer: 'relay' };
}

/** A transaction on the staking chain that the wallet broadcasts and pays for. */
function stakingTransaction(): WalletAction {
  return { kind: 'transaction', chainId: getStakingChainId(), gasPayer: 'wallet' };
}

export const ACTIONS: ActionCatalogue = {
  stake: relayedStakingSignature,
  positionOp: relayedStakingSignature,
  claimPhnm: relayedStakingSignature,
  claimChip: relayedStakingSignature,
  // Legacy USDT claims sign a Polygon domain and stay relayed by the frozen legacy relayer.
  claimUsdt: () => ({
    kind: 'chain-signature',
    chainId: LEGACY_POLYGON_CONFIG.id,
    gasPayer: 'relay',
  }),
  swap: relayedStakingSignature,
  // The wrapped USDC receive-auth for code-bearing accounts: a raw digest signature.
  swapUsdcAuth: () => ({ kind: 'raw-signature', chainId: getStakingChainId(), gasPayer: 'relay' }),
  // The EIP-7702 re-delegation authorization: a raw digest signature, embedded wallet only, the
  // type-4 transaction is submitted by poker's onboarding worker (spec §7.1).
  delegateUpgrade: () => ({
    kind: 'raw-signature',
    chainId: getStakingChainId(),
    gasPayer: 'relay',
  }),
  gaslessSend: relayedStakingSignature,
  embeddedNftTransfer: relayedStakingSignature,
  chipSend: relayedStakingSignature,
  directSend: (chainId: number) => {
    if (!getSwitchableChain(chainId)) {
      throw new Error(`No wallet action may target chain ${chainId}`);
    }
    return { kind: 'transaction', chainId, gasPayer: 'wallet' };
  },
  externalNftTransfer: stakingTransaction,
  chipRedeem: stakingTransaction,
  walletRelations: () => ({ kind: 'chainless-signature', chainId: null, gasPayer: 'relay' }),
};
